import { D, add, mul } from '../../math/num';
import type { RngState } from '../../math/rng';
import { nextFloat, weightedIndex } from '../../math/rng';
import { getPartDef, type PartDef, type AffixSlotSpec } from './partDefs';
import { RARITY_INDEX, RARITIES, type PartInstance, type Rarity, type RolledAffix } from './partTypes';

/**
 * Deterministic part generation. Every draw advances the seeded RngState, so the
 * same seed+cursor reproduces identical loot. nextId is the inventory's monotonic
 * counter (no Math.random — core purity).
 */

/** Pick up to `count` distinct affix slots, weighted, that can roll at `rarity`. */
function pickAffixSlots(slots: AffixSlotSpec[], count: number, rarity: Rarity, rng: RngState): AffixSlotSpec[] {
  const eligible = slots.filter((s) => s.rolls[rarity] !== undefined);
  const chosen: AffixSlotSpec[] = [];
  const pool = [...eligible];
  for (let i = 0; i < count && pool.length > 0; i++) {
    const idx = weightedIndex(rng, pool.map((s) => s.weight));
    chosen.push(pool[idx]!);
    pool.splice(idx, 1);
  }
  return chosen;
}

export function rollAffixes(def: PartDef, rarity: Rarity, rng: RngState): RolledAffix[] {
  const count = def.maxAffixes[rarity];
  if (count <= 0) return [];
  const slots = pickAffixSlots(def.affixSlots, count, rarity, rng);
  return slots.map((slot) => {
    const range = slot.rolls[rarity]!;
    const t = nextFloat(rng);
    const value = add(D(range.min), mul(D(range.max - range.min), D(t)));
    return { stat: slot.stat, value };
  });
}

export interface RollResult {
  instance: PartInstance;
  nextId: number;
}

export function rollPartInstance(defId: string, rarity: Rarity, rng: RngState, nextId: number): RollResult {
  const def = getPartDef(defId);
  // A part never rolls below its def's base rarity.
  const eff = RARITY_INDEX[rarity] >= RARITY_INDEX[def.baseRarity] ? rarity : def.baseRarity;
  const affixes = rollAffixes(def, eff, rng);
  const instance: PartInstance = { instanceId: `p${nextId}`, defId, rarity: eff, level: 1, affixes };
  return { instance, nextId: nextId + 1 };
}

/** Roll ONE extra affix from slots the instance hasn't already rolled — the
 *  Foundry's heat-strike bonus. Returns null if every eligible slot is taken. */
export function rollBonusAffix(def: PartDef, rarity: Rarity, existing: RolledAffix[], rng: RngState): RolledAffix | null {
  const taken = new Set(existing.map((a) => a.stat));
  const eligible = def.affixSlots.filter((s) => s.rolls[rarity] !== undefined && !taken.has(s.stat));
  if (eligible.length === 0) return null;
  const slot = eligible[weightedIndex(rng, eligible.map((s) => s.weight))]!;
  const range = slot.rolls[rarity]!;
  const t = nextFloat(rng);
  return { stat: slot.stat, value: add(D(range.min), mul(D(range.max - range.min), D(t))) };
}

/** Create a deterministic starter part (no RNG) — used for the seeded loadout. */
export function makeStarterPart(defId: string, instanceId: string, rarity: Rarity = 'common'): PartInstance {
  return { instanceId, defId, rarity, level: 1, affixes: [] };
}

export { RARITIES };
